import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core'
import { RouterModule } from '@angular/router'
import { LiveApiService } from '../live.service'

/**
 * The LIVE button in the top menu (CC, 01/10/2026): pulses red with the number of matches on air,
 * stays quiet when a match starts within the day, and is hidden when nothing is live or coming.
 */
@Component({
  selector: 'app-live-nav-button',
  standalone: true,
  imports: [RouterModule],
  template: `
    @if (live() > 0) {
      <a routerLink="/live" class="lnb lnb--on" [attr.aria-label]="live() + ' live matches'">
        <span class="lnb__dot"></span>LIVE<span class="lnb__n">{{ live() }}</span>
      </a>
    } @else if (upcoming() > 0) {
      <a routerLink="/live" class="lnb" aria-label="Live matches later today"><span class="lnb__dot"></span>LIVE</a>
    }
  `,
  styles: [`
    :host { display: inline-flex; }
    .lnb {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      min-height: 44px;  /* phone tap target, as the launcher button beside it */
      padding: 0 10px;
      border-radius: 8px;
      border: 1px solid var(--scios-border);
      color: var(--scios-text-secondary);
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-decoration: none;
      white-space: nowrap;
    }
    .lnb__dot { width: 7px; height: 7px; border-radius: 50%; background: var(--live-red); }
    .lnb--on { background: var(--live-red); border-color: var(--live-red); color: #fff; }
    .lnb--on .lnb__dot { background: #fff; animation: lnb-pulse calc(1.4s * var(--motion, 1)) ease-in-out infinite; }
    .lnb__n { padding-left: 4px; border-left: 1px solid rgba(255, 255, 255, 0.5); }
    @keyframes lnb-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
    /* Narrow phones: the pill shrinks to its dot, so the club name in the bar keeps its room.
       The aria-label still says how many matches are live. */
    @media (max-width: 399.98px) {
      .lnb { width: 44px; min-height: 44px; padding: 0; gap: 0; justify-content: center; font-size: 0; letter-spacing: 0; }
      .lnb__n { display: none; }
      .lnb__dot { width: 9px; height: 9px; }
    }
  `],
})
export class LiveNavButtonComponent implements OnInit, OnDestroy {
  private api = inject(LiveApiService)
  private readonly statuses = signal<string[]>([])
  private timer?: ReturnType<typeof setInterval>

  readonly live = computed(() => this.statuses().filter((s) => s === 'live').length)
  readonly upcoming = computed(() => this.statuses().filter((s) => s === 'scheduled' || s === 'starting').length)

  ngOnInit (): void {
    void this.load()
    this.timer = setInterval(() => void this.load(), 60_000)
  }

  ngOnDestroy (): void {
    if (this.timer) clearInterval(this.timer)
  }

  private async load (): Promise<void> {
    const cards = await this.api.cards().catch(() => [])
    this.statuses.set(cards.map((c) => c.status))
  }
}
